import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Loader2 } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { projectsAPI } from "@/api/projects";
import EnhancedImageUploader from "../EnhancedImageUploader";

interface DeliveryFormProps {
  date: Date;
  projectId: string;
  description: string;
  imageFile: File | null;
  imagePreview: string | null;
  onDateChange: (date: Date) => void;
  onProjectChange: (projectId: string) => void;
  onDescriptionChange: (description: string) => void;
  onImageChange: (file: File) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const DeliveryForm: React.FC<DeliveryFormProps> = ({
  date,
  projectId,
  description,
  imageFile,
  imagePreview,
  onDateChange,
  onProjectChange,
  onDescriptionChange,
  onImageChange,
  onSubmit,
}) => {
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch projects from the API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const data = await projectsAPI.getProjects();
        // Map the data to simplified format for the dropdown, excluding completed projects
        const mappedProjects = data
          .filter(project => project.status !== 'completed')
          .map(project => ({
            id: project.id,
            name: project.name
          }));
        setProjects(mappedProjects);
      } catch (error) {
        
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const isFormValid = () => {
    return !!date && !!projectId && description.trim() !== '';
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 pb-6 pt-4 px-4">
      {/* Date */}
      <div className="space-y-1.5">
        <label htmlFor="date" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Tarih
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-mono text-[13px] border-gray-200 bg-white hover:bg-gray-50 rounded-md h-10",
                !date && "text-gray-400",
              )}
            >
              <Calendar className="mr-2 h-4 w-4 text-gray-400 font-sans" />
              {date ? format(date, "dd.MM.yyyy") : "gg/aa/yyyy"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <CalendarComponent
              mode="single"
              selected={date}
              onSelect={(date) => date && onDateChange(date)}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Project */}
      <div className="space-y-1.5">
        <label htmlFor="project" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Proje
        </label>
        <Select
          value={projectId}
          onValueChange={onProjectChange}
        >
          <SelectTrigger className="w-full border-gray-200 bg-white rounded-md h-10 text-[13px] text-gray-900 focus:border-brand-600 focus:ring-1 focus:ring-brand-300">
            {isLoading ? (
              <div className="flex items-center text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <span className="text-[13px]">Yükleniyor...</span>
              </div>
            ) : (
              <SelectValue placeholder="Proje seçin" />
            )}
          </SelectTrigger>
          <SelectContent>
            {projects.map(project => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
            {projects.length === 0 && !isLoading && (
              <div className="py-2 px-4 text-[13px] text-gray-500">
                Proje bulunamadı
              </div>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label htmlFor="description" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Açıklama
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          className="flex min-h-[80px] w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-300 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Açıklama girin"
          required
        />
      </div>

      <EnhancedImageUploader
        imageFile={imageFile}
        imagePreview={imagePreview}
        onImageChange={onImageChange}
      />

      {/* Footer: Vazgeç + Kaydet */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
          onClick={() => {}}
        >
          Vazgeç
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white"
          disabled={!isFormValid()}
        >
          Kaydet
        </Button>
      </div>
    </form>
  );
};

export default DeliveryForm;
